//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { addToGalleries } from '@apis/bulkActionsApi';
import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { getRightsRequestStatuses } from '@apis/gatewayMediaApi';
import { fullScreenWorkflowActions } from '@galleries/actions/fullScreenWorkflowActions';
import { GALLERIES_ACTIONS } from '@galleries/constants/galleriesConstants';
import {
  FULLSCREEN_WORKFLOW_FLAGS,
  FULLSCREEN_WORKFLOW_NAMES,
} from '@galleries/models/fullScreenWorkflowModel';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { canApproveSelectedItemsToInstaShop } from '@galleries/selectors/bulk-rights-selectors/canApproveSelectedItemsToInstaShop';
import { getAllAlphabetizedGalleries } from '@galleries/selectors/getAllAlphabetizedGalleries';
import { getAllInstaShopGalleries } from '@galleries/selectors/getAllInstaShopGalleries';
import { getGalleriesForCheckboxList } from '@galleries/selectors/getGalleriesForCheckboxList';
import { isInstaShopGallery } from '@galleries/utils/isInstaShopGallery';
import { ICuralateStore } from '@models/AppState';
import * as shareApi from '@share/api/shareApi';
import { IRightsRequestState } from '@models/IItemMetadata';
import PartialCuid from '@models/PartialCuid';
import ReelId from '@models/ReelId';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import {
  clearSelectedItems as clearBulkActionsSelection,
  closeStreamsModerationDrawer as closeStreamsDrawer,
} from '@streams-drawer/actions/streams-drawer-actions';
import { createAction } from '@utils/actions';
import {
  closeConfirmModal,
  IBulkActionsActions,
} from 'app/bulk-actions/bulk-actions-actions/bulk-actions-actions';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import gaApi from 'app/initialization/ga/ga-api';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

function openModalAction(items: ISelectedItem[]) {
  return createAction({
    type: GALLERIES_ACTIONS.OPEN_ADD_TO_GALLERIES_MODAL,
    items,
  });
}

const receiveMaterializedItems = (materializedItems: IContentLibraryItem[]) => {
  return createAction({
    type: GALLERIES_ACTIONS.RECEIVE_MATERIALIZED_ITEMS,
    materializedItems,
  });
};

function closeModalAction() {
  return createAction({
    type: GALLERIES_ACTIONS.CLOSE_ADD_TO_GALLERIES_MODAL,
  });
}

const closeModal = () => async (dispatch: ReduxDispatch) => {
  dispatch(closeModalAction());
  dispatch(clearSelectedImages());
  dispatch(clearMaterializedItems());
  dispatch(createAction(closeConfirmModal()));
  dispatch(
    fullScreenWorkflowActions.stopWorkflow(
      FULLSCREEN_WORKFLOW_NAMES.ADD_TO_GALLERIES,
    ),
  );
};

function selectGallery(reelId: ReelId) {
  return createAction({
    type: GALLERIES_ACTIONS.ADD_MODAL_SELECT_GALLERY,
    reelId,
  });
}

function deselectGallery(reelId: ReelId) {
  return createAction({
    type: GALLERIES_ACTIONS.ADD_MODAL_DESELECT_GALLERY,
    reelId,
  });
}

function setSelectedGalleries(reelIds: ReelId[]) {
  return createAction({
    type: GALLERIES_ACTIONS.ADD_MODAL_SET_SELECTED_GALLERIES,
    reelIds,
  });
}

function selectImage(selectedItem: IContentLibraryItem) {
  return createAction({
    type: GALLERIES_ACTIONS.SELECT_IMAGE,
    selectedItem,
  });
}

function deselectImage(deselectedItem: IContentLibraryItem) {
  return createAction({
    type: GALLERIES_ACTIONS.DESELECT_IMAGE,
    deselectedItem,
  });
}

function clearSelectedImages() {
  return createAction({
    type: GALLERIES_ACTIONS.CLEAR_IMAGES,
  });
}

function clearMaterializedItems() {
  return createAction({
    type: GALLERIES_ACTIONS.CLEAR_MATERIALIZED_ITEMS,
  });
}

function setIsLoading(isLoading: boolean) {
  return createAction({
    type: GALLERIES_ACTIONS.IS_LOADING,
    isLoading,
  });
}

function receiveRightsRequestStates(
  rightsRequestMap: Record<PartialCuid, IRightsRequestState>,
) {
  return createAction({
    type: GALLERIES_ACTIONS.RECEIVE_RIGHTS_REQUEST_STATES,
    rightsRequestMap,
  });
}

function toggleBazaarvoiceSyndication() {
  return createAction({
    type: GALLERIES_ACTIONS.TOGGLE_BAZAAR_VOICE_SYNDICATION,
  });
}

export type GalleriesAddToGalleriesModalActionType = ReturnType<
  | typeof openModalAction
  | typeof selectGallery
  | typeof deselectGallery
  | typeof setSelectedGalleries
  | typeof closeModalAction
  | typeof receiveMaterializedItems
  | typeof selectImage
  | typeof deselectImage
  | typeof clearSelectedImages
  | typeof clearMaterializedItems
  | typeof setIsLoading
  | typeof receiveRightsRequestStates
  | typeof toggleBazaarvoiceSyndication
>;

export const galleriesAddToGalleriesModalActions = {
  closeModal,
  selectGallery,
  deselectGallery,
  toggleBazaarvoiceSyndication,

  toggleImage:
    (toggledImage: IContentLibraryItem) =>
    (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const selectedImages =
        getState().galleries.addToGalleriesModal.currentlySelectedItems;

      if (selectedImages[toggledImage.id]) {
        dispatch(deselectImage(toggledImage));
      } else {
        dispatch(selectImage(toggledImage));
      }
    },

  openModal:
    (items: ISelectedItem[]) =>
    async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(
        fullScreenWorkflowActions.startWorkflow(
          FULLSCREEN_WORKFLOW_NAMES.ADD_TO_GALLERIES,
        ),
      );

      dispatch(openModalAction(items));

      const partialCuids = items.map((item) => item.cuid);
      const getContentItemsPromise = getContentLibraryItems({
        itemIds: partialCuids,
      });

      const allGalleries = getAllAlphabetizedGalleries(getState());
      const getGalleriesPromise = (() => {
        if (allGalleries.length > 0) {
          return Promise.resolve(allGalleries);
        } else {
          return dispatch(galleriesDataActions.getGalleries());
        }
      })();
      dispatch(setIsLoading(true));

      const rightsRequestMapPromise = getRightsRequestStatuses(partialCuids);

      Promise.all([
        getContentItemsPromise,
        getGalleriesPromise,
        rightsRequestMapPromise,
      ]).then(([contentItems, , { data: rightsRequestMap }]) => {
        dispatch(receiveMaterializedItems(contentItems.contentItems));
        contentItems.contentItems.forEach((item) =>
          dispatch(selectImage(item)),
        );

        dispatch(receiveRightsRequestStates(rightsRequestMap));

        const galleriesForCheckboxes = getGalleriesForCheckboxList(getState());
        const isInstashopEnabled = getState().session.features.instashop;

        if (
          isInstashopEnabled &&
          !canApproveSelectedItemsToInstaShop(getState())
        ) {
          // If not all content is brand owned, we shouldn't approve it to InstaShop galleries
          const initialSelectedGalleries = galleriesForCheckboxes
            .filter((gallery) => {
              return !isInstaShopGallery(gallery);
            })
            .map((gallery) => gallery.reelId);
          dispatch(setSelectedGalleries(initialSelectedGalleries));
        } else {
          const reelIds = galleriesForCheckboxes.map(
            (gallery) => gallery.reelId,
          );
          dispatch(setSelectedGalleries(reelIds));
        }

        dispatch(setIsLoading(false));
      });
    },

  addSelectedItemsToGallery:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const galleriesState = getState().galleries;
      const {
        currentlySelectedItems,
        gallerySelection,
        initiallySelectedItems,
      } = galleriesState.addToGalleriesModal;
      const allGalleries = getAllAlphabetizedGalleries(getState());
      const isInstashopEnabled = getState().session.features.instashop;
      const canApproveToInstaShop =
        canApproveSelectedItemsToInstaShop(getState());
      const allInstaShopReelIds = getAllInstaShopGalleries(getState()).map(
        (gallery) => gallery.reelId,
      );

      const selectedGalleries = PojoSet.toArray(gallerySelection).map(
        (reelId) => allGalleries.find((gallery) => gallery.reelId === reelId),
      );

      const reelIds = (() => {
        let selectedReelIds = [];
        const dontApproveToInstaShopGalleries =
          isInstashopEnabled && !canApproveToInstaShop;

        for (const gallery of selectedGalleries) {
          // If we see that the user wants to approve to InstaShops, then we should approve
          // to all InstaShops
          if (isInstaShopGallery(gallery) && !dontApproveToInstaShopGalleries) {
            selectedReelIds = selectedReelIds.concat(allInstaShopReelIds);
          } else if (!isInstaShopGallery(gallery)) {
            selectedReelIds.push(gallery.reelId);
          }
        }

        return selectedReelIds;
      })();

      const partialCuids = Object.values<IContentLibraryItem>(
        currentlySelectedItems,
      ).map((item) => PartialCuid.build(item));

      const isBazaarvoiceSyndicationEnabled =
        getIsBazaarvoiceNetworkSyndicationFrontendEnabled(getState());

      const isBazaarvoiceSyndicationSelected =
        getState().galleries.addToGalleriesModal
          .isBazaarvoiceSyndicationSelected;
      const shouldSyndicateToBazaarvoiceNetwork =
        isBazaarvoiceSyndicationEnabled && isBazaarvoiceSyndicationSelected;

      const promises = [];

      if (shouldSyndicateToBazaarvoiceNetwork) {
        promises.push(shareApi.shareMedia(partialCuids));
      }

      promises.push(
        // Hack to appease the typescript gods.
        // Ensures we're pushing a promise of type Promise<void> to match the type of the Share promise above.
        addToGalleries(partialCuids, reelIds).then((res) => {
          return null;
        }),
      );

      await Promise.all(promises);

      gaApi.recordEvent(
        gaApi.CATEGORY_GALLERIES,
        gaApi.ACTION_GALLERIES_ADD_ITEMS_TO_GALLERIES,
        'numberOfGalleries',
        reelIds.length.toString(),
      );

      const BulkActionsActions: IBulkActionsActions = getBulkActionsActions();

      BulkActionsActions.trackBulkSelectedMediaItems(
        initiallySelectedItems.filter((item) =>
          partialCuids.includes(item.cuid),
        ),
        MediaTrackingEventName.AddedToGallery,
      );

      for (const item of initiallySelectedItems) {
        dispatch(
          BulkActionsActions.updateItemOverlayDetails(
            item.cuid,
            /* forceRefresh */ true,
          ),
        );

        item.onAddToGalleryCallback();
      }

      dispatch(galleriesDataActions.getGalleryItems());
      dispatch(clearBulkActionsSelection());
      dispatch(
        fullScreenWorkflowActions.setFlag(
          FULLSCREEN_WORKFLOW_FLAGS.ADDED_TO_GALLERIES,
          true,
        ),
      );
      dispatch(closeStreamsDrawer());
    },
};
